/**
 * Générateur — Comparer et ranger dans l'ordre croissant et décroissant des
 * nombres décimaux relatifs.
 * Attendu ID : 707 | Niveau : 5ème | Domaine : Nombres relatifs (cluster 3)
 *
 * Plan de cartes :
 *
 *   Carte « comparer » — Ex.9 Complète par >, < ou =
 *     tableau colonnesLibres (colonnes <, >, = — précédents gen_604/653) +
 *     qcm_unique (texte échappé &lt;/&gt;). Rotation des TROIS cas du cadrage :
 *     deux positifs, signes contraires, deux négatifs — avec les pièges du doc
 *     (chiffres permutés 5,34/3,54 ; décimales de longueurs différentes
 *     -9,27/-9,272 ; cas d'égalité).
 *     ⚠️ Jamais de < ou > brut dans une phrase à trous (rendu innerHTML).
 *
 *   Carte « ranger » — Ex.11 Ordre croissant
 *     ordonner : 6-7 nombres mixtes positifs/négatifs, valeurs deux à deux
 *     distinctes, sens croissant OU décroissant en rotation.
 *
 *   Carte « encadrer » — Ex.15 Encadrer par deux entiers consécutifs
 *     texte_trou_saisie REFORMULÉ sans symbole (« est compris entre {{a}},
 *     l'entier juste en dessous, et {{b}} … ») — l'ordre des trous est fixé
 *     par la phrase, pas par l'inégalité ; qcm_unique avec chaînes complètes
 *     (croissantes ET décroissantes : l'alternance de sens du doc), piège
 *     central côté négatif (-2 < -2,3 < -1). Grands nombres (type -3 251,2)
 *     en rotation.
 *
 *   Carte « intrus » — Ex.10 Le nombre qui n'est pas à sa place
 *     qcm_unique. UNICITÉ PAR CONSTRUCTION (le doc signale le piège b/c) :
 *     on part d'une liste triée et on DÉPLACE un élément d'au moins 2 rangs —
 *     retirer cet élément répare la chaîne, retirer tout autre la laisse
 *     cassée (le solveur re-vérifie l'unicité en testant chaque retrait).
 *
 *   Carte « verbaliser » — Créé n°13 Verbaliser la comparaison
 *     texte_trou_drag + qcm_unique. Reprend la formulation du cadrage
 *     (« -7 c'est 2 de moins que -5 »), automatisme B2, distances lues sur la
 *     droite — aucune règle d'addition des relatifs.
 *
 * Arithmétique en MILLIÈMES entiers. Signe « - » = tiret clavier partout.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeOrdonner, makeTexteTrou, basePayload,
    filtrerDistracteurs,
} from '../../../utils';

const ATTENDU_ID = 707;

const L_COMPARER = 'Comparer deux nombres relatifs';
const L_RANGER = 'Ranger dans l\'ordre';
const L_ENCADRER = 'Encadrer par deux entiers consécutifs';
const L_INTRUS = 'Le nombre qui n\'est pas à sa place';
const L_VERBALISER = 'Verbaliser la comparaison';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// millièmes → écriture française ("-9,272", "0,05", "-3251,2")
const frM = (m) => {
    const s = m < 0 ? '-' : '';
    const a = Math.abs(m);
    const ent = Math.floor(a / 1000);
    const frac = String(a % 1000).padStart(3, '0').replace(/0+$/, '');
    return s + (frac ? `${ent},${frac}` : String(ent));
};
const signeM = (m) => (m === 0 ? '0' : m < 0 ? frM(m) : `+${frM(m)}`);

// ═════════ Carte 1 — Comparer (Ex.9) ═════════

/** Tire une paire (a, b) en millièmes selon le cas du cadrage. */
const tirerPaire = (cas) => {
    if (cas === 'egaux') {
        const v = (randomChoice([1, -1])) * (randInt(11, 199) * 100 + randomChoice([0, randInt(1, 9) * 10]));
        return [v, v];
    }
    if (cas === 'mixte') {
        // signes contraires — valeurs absolues PROCHES (le signe seul décide)
        const a = randInt(20, 199) * 100;
        const b = a + randomChoice([-100, 100, 0]) || a + 100;
        return randomChoice([true, false]) ? [-a, b] : [a, -b];
    }
    const signe = cas === 'neg_neg' ? -1 : 1;
    if (randomChoice([true, false])) {
        // piège « longueurs de décimales » : -9,27 vs -9,272
        const base = randInt(11, 99) * 100 + randInt(1, 9) * 10;
        const long = base + randomChoice([2, 5, 8]) * (randomChoice([true, false]) ? 1 : -1);
        return [signe * base, signe * long];
    }
    // piège « chiffres permutés » : 5,34 vs 3,54
    const c1 = randInt(1, 9);
    let c2 = randInt(1, 9);
    if (c2 === c1) c2 = (c1 % 9) + 1;
    const c3 = randInt(1, 9);
    return [signe * (c1 * 1000 + c2 * 100 + c3 * 10), signe * (c2 * 1000 + c1 * 100 + c3 * 10)];
};

const symboleDe = (a, b) => (a < b ? '<' : a > b ? '>' : '=');

const CAS_COMPARER = ['pos_pos', 'neg_neg', 'mixte', 'egaux'];

const genComparerTableau = () => {
    const cas = shuffle(CAS_COMPARER);
    const paires = cas.map(c => tirerPaire(c));
    const colonnes = ['<', '>', '='];
    const lignes = paires.map(([a, b]) => `${frM(a)} … ${frM(b)}`);
    const bonnes = paires.map(([a, b]) => colonnes.indexOf(symboleDe(a, b)));
    const tableEl = {
        type: 'table',
        config: { type: 'colonnesLibres', colonnes, lignes, bonnesReponses: bonnes },
    };
    const br = lignes.map((l, i) => `[${l}|${colonnes[bonnes[i]]}]`).join('');
    const payload = basePayload('qcm',
        '<p><b>Pour chaque ligne, coche le symbole qui convient</b> (&lt;, &gt; ou =).</p>',
        { choix: [], bonne_reponse: br },
        opts(L_COMPARER),
        { version: 1, elements: [tableEl] });
    payload._meta.params = { paires };
    return payload;
};

const genComparerQcm = () => {
    const [a, b] = tirerPaire(randomChoice(CAS_COMPARER));
    const correct = symboleDe(a, b);
    const payload = makeQcm(['<', '>', '='], correct,
        `<p>Complète avec &lt;, &gt; ou = : <strong>${frM(a)} … ${frM(b)}</strong></p>`,
        opts(L_COMPARER));
    payload._meta.params = { a, b };
    return payload;
};

// ═════════ Carte 2 — Ranger (Ex.11) ═════════

const genRangerOrdonner = () => {
    const valeurs = new Set();
    for (let essai = 0; essai < 80 && valeurs.size < 7; essai++) {
        const signe = valeurs.size < 4 ? -1 : 1; // ≥ 4 négatifs, ≥ 3 positifs
        valeurs.add(signe * (randInt(2, 89) * 100 + randomChoice([0, randInt(1, 9) * 10])));
    }
    [-8300, -3100, -2600, -200, 2700, 5000, 7100].forEach(v => { if (valeurs.size < 7) valeurs.add(v); });
    const croissant = randomChoice([true, false]);
    const tries = [...valeurs].sort((x, y) => (croissant ? x - y : y - x)).slice(0, 7);
    const payload = makeOrdonner(tries.map(frM),
        `<p><b>Range ces nombres dans l'ordre ${croissant ? 'croissant (du plus petit au plus grand)' : 'décroissant (du plus grand au plus petit)'}.</b></p>`,
        opts(L_RANGER));
    payload._meta.params = { croissant };
    return payload;
};

// ═════════ Carte 3 — Encadrer (Ex.15) ═════════

/** Décimal NON entier, petit (±13) ou grand (±5000) selon le tirage. */
const tirerNonEntier = () => {
    const grand = randomChoice([true, false, false]);
    const ent = grand ? randInt(100, 4999) : randInt(0, 12);
    const frac = randInt(1, 9) * 100 + randomChoice([0, randInt(1, 9) * 10]);
    return (randomChoice([true, false]) ? -1 : 1) * (ent * 1000 + frac);
};

const genEncadrerSaisie = () => {
    const x = tirerNonEntier();
    const a = Math.floor(x / 1000);   // entier juste en dessous
    const b = a + 1;                  // entier juste au-dessus
    const phrase = `${frM(x)} est compris entre {{${a}}} (l'entier juste en dessous) et {{${b}}} (l'entier juste au-dessus).`;
    const payload = makeTexteTrou(phrase,
        '<p><b>Encadre ce nombre par deux entiers consécutifs.</b> Écris les entiers négatifs avec le signe - (ex. : -3).</p>',
        { ...opts(L_ENCADRER), mode: 'saisie' });
    payload._meta.params = { x, a, b };
    return payload;
};

const genEncadrerQcm = () => {
    const x = tirerNonEntier();
    const a = Math.floor(x / 1000);
    const b = a + 1;
    const croissant = randomChoice([true, false]);
    const X = frM(x);
    const chaine = (g, d) => (croissant ? `${g} < ${X} < ${d}` : `${d} > ${X} > ${g}`);
    const correct = chaine(a, b);
    const distracteurs = [
        chaine(b, a),           // bornes échangées (sens de lecture)
        chaine(a + 1, b + 1),   // le piège négatif du doc : -2 < -2,3 < -1
        chaine(a - 1, a),       // décalé d'un cran vers le bas
    ];
    const payload = makeQcm([correct, ...distracteurs], correct,
        `<p><b>Quel encadrement de ${X} par deux entiers consécutifs est correct ?</b></p>`,
        opts(L_ENCADRER));
    payload._meta.params = { x, a, b, croissant };
    return payload;
};

// ═════════ Carte 4 — L'intrus (Ex.10) ═════════

/**
 * Chaîne de 5 nombres presque triée : un élément d'une liste triée est
 * DÉPLACÉ d'au moins 2 rangs → son retrait (et lui seul) répare la chaîne.
 */
const genIntrusQcm = () => {
    // 5 valeurs proches, même partie entière négative le plus souvent (style doc)
    const base = -(randInt(2, 99) * 1000 + randInt(0, 9) * 100);
    const valeurs = [base];
    for (let i = 1; i < 5; i++) valeurs.push(valeurs[i - 1] + randInt(2, 9) * 10);
    const croissant = randomChoice([true, false]);
    const triees = croissant ? valeurs : [...valeurs].reverse();

    // déplacement d'au moins 2 rangs (k → p)
    const k = randomChoice([0, 1]);
    const p = k + randomChoice([2, 3]);
    const intrus = triees[k];
    const chaine = triees.filter((_, i) => i !== k);
    chaine.splice(p, 0, intrus);

    const sep = croissant ? ' &lt; ' : ' &gt; ';
    const correct = frM(intrus);
    const autres = chaine.map(frM).filter(s => s !== correct);
    const payload = makeQcm([correct, ...autres.slice(0, 3)], correct,
        `<p>Cette chaîne est presque rangée, mais un nombre n'est pas à sa place :</p>`
        + `<p><strong>${chaine.map(frM).join(sep)}</strong></p>`
        + '<p><b>Quel nombre faut-il retirer pour que la chaîne devienne vraie ?</b></p>',
        opts(L_INTRUS));
    payload._meta.params = { intrus, croissant };
    return payload;
};

// ═════════ Carte 5 — Verbaliser (Créé n°13) ═════════

/** Tire une situation de verbalisation (valeurs entières, distances lisibles). */
const tirerVerbalisation = () => {
    const type = randomChoice(['pos_moins', 'neg_moins', 'cible', 'plus']);
    if (type === 'pos_moins') {
        const p = randInt(1, 12);
        const d = randInt(1, 9);
        return { type, phrase: (t) => `${p} c'est ${t} de moins que ${p + d}.`, reponse: String(d) };
    }
    if (type === 'neg_moins') {
        const p = randInt(1, 9);
        const d = randInt(1, 9);
        return { type, phrase: (t) => `-${p + d} c'est ${t} de moins que -${p}.`, reponse: String(d) };
    }
    if (type === 'cible') {
        const x = -randInt(1, 9);
        const d = randInt(1, 12);
        return { type, phrase: (t) => `${x} c'est ${d} de moins que ${t}.`, reponse: signeM((x + d) * 1000) };
    }
    const y = -randInt(2, 12);
    const d = randInt(1, 9);
    return { type, phrase: (t) => `${y + d} c'est ${t} de plus que ${y}.`, reponse: String(d) };
};

const genVerbaliserDrag = () => {
    const v = tirerVerbalisation();
    const phrase = v.phrase(`{{${v.reponse}}}`);
    const n = parseInt(v.reponse.replace('+', ''), 10);
    const candidats = [String(n + 1), String(n - 1), v.reponse.startsWith('+') ? `-${v.reponse.slice(1)}` : `-${v.reponse}`];
    const payload = makeTexteTrou(phrase,
        '<p><b>Complète.</b> (Aide-toi de la droite graduée dans ta tête : on compte les graduations.)</p>',
        { ...opts(L_VERBALISER), mode: 'drag', distracteurs: filtrerDistracteurs(phrase, candidats).slice(0, 3) });
    payload._meta.params = { type: v.type, reponse: v.reponse };
    return payload;
};

const genVerbaliserQcm = () => {
    let v = tirerVerbalisation();
    for (let essai = 0; essai < 10 && v.type === 'cible'; essai++) v = tirerVerbalisation();
    if (v.type === 'cible') v = { type: 'pos_moins', phrase: (t) => `5 c'est ${t} de moins que 7.`, reponse: '2' };
    const n = parseInt(v.reponse, 10);
    const distracteurs = [];
    [String(n + 1), String(n - 1), `-${n}`, String(n + 2)].forEach(c => {
        if (distracteurs.length < 3 && c !== v.reponse && !distracteurs.includes(c) && c !== '0') distracteurs.push(c);
    });
    const payload = makeQcm([v.reponse, ...distracteurs], v.reponse,
        `<p><b>Complète : ${v.phrase('…')}</b></p>`,
        opts(L_VERBALISER));
    payload._meta.params = { type: v.type, reponse: v.reponse };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    comparer_tableau: genComparerTableau,
    comparer_qcm_unique: genComparerQcm,
    ranger_ordonner: genRangerOrdonner,
    encadrer_texte_trou_saisie: genEncadrerSaisie,
    encadrer_qcm_unique: genEncadrerQcm,
    intrus_qcm_unique: genIntrusQcm,
    verbaliser_texte_trou_drag: genVerbaliserDrag,
    verbaliser_qcm_unique: genVerbaliserQcm,
};

const FORMAT_LABELS = {
    tableau: 'Tableau', qcm_unique: 'QCM', ordonner: 'Ordonner',
    texte_trou_saisie: 'Saisie trous', texte_trou_drag: 'Drag',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'comparer', label: L_COMPARER, formatsList: buildFormats('comparer') },
    { id: 'ranger', label: L_RANGER, formatsList: buildFormats('ranger') },
    { id: 'encadrer', label: L_ENCADRER, formatsList: buildFormats('encadrer') },
    { id: 'intrus', label: L_INTRUS, formatsList: buildFormats('intrus') },
    { id: 'verbaliser', label: L_VERBALISER, formatsList: buildFormats('verbaliser') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
